// ═══════════════════════════════════════════════════════════════════════════════
//  src/advanced/SituacionRoom.jsx
//  FORMATO 07 — LA SITUACIÓN
//
//  Una conversación de ida y vuelta con alguien que no te lo va a poner fácil,
//  y al final un dictamen: ¿conseguiste lo que ibas a conseguir?
//
//  ── QUÉ LO SEPARA DE LOS OTROS SEIS ─────────────────────────────────────────
//  Los otros seis miden cómo hablas. Éste mide si LOGRASTE algo. Es la única
//  pregunta que un jefe, un cliente o una aduana van a hacerte, y por eso es lo
//  único que se puede prometer sin vender humo: «tenemos todas las herramientas
//  que necesitas y el esfuerzo lo tienes tú».
//
//  ── POR QUÉ EL VEREDICTO PUEDE DECIR NO ─────────────────────────────────────
//  Un veredicto que siempre felicita no es un entrenamiento, es un adorno, y el
//  alumno lo huele en la segunda sesión. Este juez dice no cuando es no —está
//  medido contra un guion que parecía bueno y no cerró el acuerdo—, y cita las
//  palabras del alumno para que el no sea comprobable y no una opinión.
//
//  ── POR QUÉ NO TE CORRIGE EL INGLÉS MIENTRAS HABLAS ─────────────────────────
//  Porque en una negociación de verdad nadie te corrige: te dice que no. El
//  personaje aguanta su papel hasta el final. La corrección, si toca, llega en
//  el dictamen.
//
//  ── LA CONFIGURACIÓN ES EL FORMATO ──────────────────────────────────────────
//  Rob: «aquí entran todos los negocios y formatos. Cada acción que creemos debe
//  tener configuración para que sea adaptativa a cada preferencia del usuario».
//
//  Tres perillas —sector, situación y cuánto aprieta el otro— dan 180 escenas
//  distintas, y ninguna se genera con IA: el servidor las compone de piezas, así
//  que salen igual mil veces y cuestan cero. La cuarta perilla es el acento de
//  la voz del otro.
//
//  Y la elección SE RECUERDA. Un vendedor que siempre entra a negociar no debe
//  contestar el mismo formulario cada día: la segunda vez ya está puesto y sólo
//  pulsa empezar. Eso es lo que hace que sea configuración y no un trámite.
//
//  ── EL SUELO SE VE MIENTRAS HABLAS ──────────────────────────────────────────
//  El servidor no dictamina por debajo de un mínimo de intercambio, porque
//  juzgar tres frases sería inventado. Ese mínimo se muestra EN VIVO, no al
//  final: un criterio que se descubre cuando ya no puedes hacer nada es una
//  trampa, y además sería la segunda vez que el alumno se lleva la sorpresa.
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react';

import { createRecognition, isRecognitionAvailable } from '../utils/browserSpeech';
import { say, stop as stopSpeech, isAvailable as canSpeak,
         availableAccents, ACCENTS } from './advancedSpeech';
import { getSituacionOptions, situacionTurn, situacionVerdict } from './advancedApi';
import { textos } from './situacionTextos';

const PREF = 'nsl_situacion_pref';   // la configuración del alumno, en su equipo

// LA MISMA clave que el coach, a propósito. Un alumno que ya puso «inglés» allí
// no tiene por qué volver a decirlo aquí: es una preferencia de la persona, no
// de la pantalla. Si algún día hay un ajuste global de idioma, esto se engancha
// a él sin tocar la sala.
const CLAVE_IDIOMA = 'nsl_coach_idioma';

function idiomaElegido() {
  try { return localStorage.getItem(CLAVE_IDIOMA) || 'auto'; } catch { return 'auto'; }
}
const TOPE_TURNOS = 14;              // el servidor acepta 30; aquí sobra con esto

/** Lee la preferencia guardada. Si está corrupta o no existe, no pasa nada:
 *  la pantalla se pinta con las primeras opciones del catálogo. */
function preferencia() {
  try {
    const p = JSON.parse(localStorage.getItem(PREF) || '{}');
    return (p && typeof p === 'object') ? p : {};
  } catch { return {}; }
}

function guardaPreferencia(p) {
  try { localStorage.setItem(PREF, JSON.stringify(p)); } catch { /* modo privado */ }
}

const palabras = (t) => (String(t || '').trim().match(/\S+/g) || []).length;

// `idiomaInicial` lo trae la sala, que ya lo preguntó para pintar las tarjetas.
// Sin él, esta pantalla arrancaría en español y saltaría al inglés cuando llega
// el catálogo: un parpadeo de idioma que se ve y que parece un error.
export default function SituacionRoom({ onExit, idiomaInicial = 'es' }) {
  const [phase, setPhase] = useState('setup');   // setup|brief|live|grading|verdict|error
  const [opts, setOpts] = useState(null);
  const [error, setError] = useState(null);

  // El idioma EFECTIVO lo resuelve el servidor —es el único que sabe su nivel—
  // y llega con el catálogo. Hasta entonces no se pinta nada más que el
  // «preparando», así que no hay parpadeo de un idioma al otro.
  const [idioma, setIdioma] = useState(idiomaInicial);
  const T = textos(idioma);

  // Las cuatro perillas, arrancadas de lo que el alumno eligió la última vez.
  const guardada = preferencia();
  const [sector, setSector] = useState(guardada.sector || null);
  const [situacion, setSituacion] = useState(guardada.situacion || null);
  const [exigencia, setExigencia] = useState(guardada.exigencia || 'firme');
  const [accent, setAccent] = useState(guardada.accent || 'en-US');

  const [turnos, setTurnos] = useState([]);      // [{de:'alumno'|'otro', texto}]
  const [dictando, setDictando] = useState(false);
  const [borrador, setBorrador] = useState('');  // lo que se está oyendo ahora
  const [esperando, setEsperando] = useState(false);
  const [verdict, setVerdict] = useState(null);
  const [aMano, setAMano] = useState(false);     // el equipo no oye: se escribe

  const recRef = useRef(null);
  const finalText = useRef('');
  const startedAt = useRef(0);
  const enviado = useRef(false);
  const finDelHilo = useRef(null);

  const canHear = isRecognitionAvailable();
  const acentos = availableAccents();

  /* ── El catálogo, al entrar. No gasta IA, así que se pide sin miedo ─────── */
  useEffect(() => {
    let vivo = true;
    getSituacionOptions(idiomaElegido())
      .then((o) => {
        if (!vivo) return;
        setOpts(o);
        if (o.idioma) setIdioma(o.idioma);
        // Si nunca eligió, se deja puesto lo primero: la pantalla no arranca
        // vacía y el botón de empezar funciona desde el primer segundo.
        setSector((s) => s || o.sectores[0]?.id);
        setSituacion((s) => s || o.situaciones[0]?.id);
      })
      .catch((e) => { if (vivo) { setError(e.message); setPhase('error'); } });
    return () => { vivo = false; };
  }, []);

  const cleanup = useCallback(() => {
    stopSpeech();
    try { recRef.current?.stop(); } catch { /* ya detenido */ }
    recRef.current = null;
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const conf = { sector, situacion, exigencia };

  const mios = turnos.filter((t) => t.de === 'alumno');
  const misPalabras = mios.reduce((n, t) => n + palabras(t.texto), 0);
  const minimo = opts?.minimos || { turnos: 3, palabras: 60 };
  const faltanTurnos = Math.max(0, minimo.turnos - mios.length);
  const faltanPalabras = Math.max(0, minimo.palabras - misPalabras);
  const juzgable = faltanTurnos === 0 && faltanPalabras === 0;

  /* ── Empezar: el otro abre la escena. Ese turno no cuesta IA ────────────── */
  const empieza = async () => {
    setError(null);
    guardaPreferencia({ sector, situacion, exigencia, accent });
    setTurnos([]);
    setVerdict(null);
    setBorrador('');
    enviado.current = false;
    setAMano(!canHear);
    setEsperando(true);
    setPhase('live');
    startedAt.current = performance.now();

    try {
      const r = await situacionTurn({ ...conf, turnos: [] });
      setTurnos([{ de: 'otro', texto: r.reply_en }]);
      if (canSpeak()) say(r.reply_en, { accent, rate: 1.05 });
    } catch (e) {
      setError(e.message);
      setPhase('error');
    } finally { setEsperando(false); }
  };

  /* ── Un turno del alumno ────────────────────────────────────────────────── */
  const oye = () => {
    if (!canHear) { setAMano(true); return; }
    finalText.current = '';
    setBorrador('');
    setDictando(true);

    const rec = createRecognition({
      onPartial: (t) => { finalText.current = t; setBorrador(t); },
      onFinal: (t) => { finalText.current = t || finalText.current; },
      onError: () => { /* se manda lo que se haya oído */ },
    });
    recRef.current = rec;
    rec?.start();
  };

  const manda = async (texto) => {
    const dicho = String(texto || '').trim();
    setDictando(false);
    try { recRef.current?.stop(); } catch { /* ya detenido */ }
    if (!dicho) return;

    const conMio = [...turnos, { de: 'alumno', texto: dicho }];
    setTurnos(conMio);
    setBorrador('');
    setEsperando(true);

    try {
      const r = await situacionTurn({ ...conf, turnos: conMio.slice(-TOPE_TURNOS) });
      setTurnos([...conMio, { de: 'otro', texto: r.reply_en }]);
      if (canSpeak()) say(r.reply_en, { accent, rate: 1.05 });
    } catch (e) {
      // Un fallo de red no tira la conversación: se queda lo dicho y se avisa.
      setError(e.message);
    } finally {
      setEsperando(false);
      // El final del hilo, para que el turno nuevo quede a la vista.
      setTimeout(() => finDelHilo.current?.scrollIntoView({ behavior: 'smooth' }), 60);
    }
  };

  /* ── Cerrar y que se dictamine ──────────────────────────────────────────── */
  const cierra = async () => {
    if (enviado.current) return;
    enviado.current = true;
    cleanup();
    setDictando(false);

    const seconds = (performance.now() - startedAt.current) / 1000;
    setPhase('grading');
    try {
      setVerdict(await situacionVerdict({
        ...conf, turnos, seconds, idioma: idiomaElegido(),
      }));
      setPhase('verdict');
    } catch (e) {
      setError(e.message);
      setPhase('error');
    }
  };

  const otraVez = () => {
    enviado.current = false;
    setVerdict(null);
    setTurnos([]);
    setError(null);
    setPhase('setup');
  };

  const nombre = (lista, id) => lista?.find((x) => x.id === id)?.label || id;
  const retoDe = (id) => opts?.situaciones?.find((x) => x.id === id)?.reto;

  /* ══ RENDER ══════════════════════════════════════════════════════════════ */

  if (phase === 'error') {
    return (
      <div className="ar-screen">
        <div className="ar-fail">{error}</div>
        <div className="ar-actions">
          <button type="button" className="ar-btn ar-btn-ghost" onClick={onExit}>
            Volver a la sala
          </button>
        </div>
      </div>
    );
  }

  const cabecera = (
    <div className="ar-hud">
      <button type="button" className="ar-quit" onClick={() => { cleanup(); onExit(); }}>
        {T.salir}
      </button>
      <span className="ar-badge">{T.formato}</span>
      <span className="ar-spacer" />
      <span className="ar-badge">B2 – C2</span>
    </div>
  );

  /* ── 1. LA CONFIGURACIÓN ────────────────────────────────────────────────── */
  if (phase === 'setup') {
    if (!opts) {
      return (
        <div className="ar-screen" style={{ '--ar-mc': 'var(--ar-f-situacion)' }}>
          {cabecera}
          <div className="ar-spin" />
          <p className="ar-lbl" style={{ textAlign: 'center' }}>{T.preparando}</p>
        </div>
      );
    }

    const total = opts.sectores.length * opts.situaciones.length * opts.exigencia.length;

    return (
      <div className="ar-screen" style={{ '--ar-mc': 'var(--ar-f-situacion)' }}>
        {cabecera}

        <div className="ar-card-q">
          <span className="ar-lbl">{T.monta}</span>
          <p className="ar-scene" style={{ marginTop: '.5rem' }}>
            {T.montaTexto} <b>{T.montaTextoFuerte}</b>.
          </p>
        </div>

        <div className="ar-knobs">
          <div className="ar-knob">
            <span className="ar-lbl">{T.tuSector}</span>
            <div className="ar-chips">
              {opts.sectores.map((s) => (
                <button
                  key={s.id} type="button"
                  className={`ar-chip ${sector === s.id ? 'ar-chip-on' : ''}`}
                  onClick={() => setSector(s.id)}
                >{s.label}</button>
              ))}
            </div>
          </div>

          <div className="ar-knob">
            <span className="ar-lbl">{T.laConversacion}</span>
            <div className="ar-chips">
              {opts.situaciones.map((s) => (
                <button
                  key={s.id} type="button"
                  className={`ar-chip ${situacion === s.id ? 'ar-chip-on' : ''}`}
                  onClick={() => setSituacion(s.id)}
                >{s.label}</button>
              ))}
            </div>
            {retoDe(situacion) && <p className="ar-knob-why">{retoDe(situacion)}</p>}
          </div>

          <div className="ar-knob">
            <span className="ar-lbl">{T.cuantoAprieta}</span>
            <div className="ar-chips">
              {opts.exigencia.map((s) => (
                <button
                  key={s.id} type="button"
                  className={`ar-chip ${exigencia === s.id ? 'ar-chip-on' : ''}`}
                  onClick={() => setExigencia(s.id)}
                >{s.label}</button>
              ))}
            </div>
            {/* Que quede claro que esto NO es el nivel de inglés: un C1 puede
                querer calentar en fácil y un B2 valiente querer que le aprieten. */}
            <p className="ar-knob-why">
              {T.exigenciaPorque1} <b>{T.exigenciaPorqueDuro}</b>{' '}
              {T.exigenciaPorque2}
            </p>
          </div>

          {acentos.length > 1 && (
            <div className="ar-knob">
              <span className="ar-lbl">{T.suAcento}</span>
              <div className="ar-chips">
                {acentos.map((a) => (
                  <button
                    key={a} type="button"
                    className={`ar-chip ${accent === a ? 'ar-chip-on' : ''}`}
                    onClick={() => setAccent(a)}
                  >{ACCENTS[a].flag}{' '}
                    {idioma === 'en' ? ACCENTS[a].label_en : ACCENTS[a].label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <p className="ar-note">{T.catalogo(total)}</p>

        {!canHear && (
          <p className="ar-note">{T.sinMicro}</p>
        )}

        <div className="ar-actions">
          <button type="button" className="ar-btn" onClick={empieza}>
            {T.entrar}
          </button>
          <button type="button" className="ar-btn ar-btn-ghost" onClick={onExit}>
            {T.volver}
          </button>
        </div>
      </div>
    );
  }

  /* ── 2. LA CONVERSACIÓN ─────────────────────────────────────────────────── */
  if (phase === 'live') {
    return (
      <div className="ar-screen" style={{ '--ar-mc': 'var(--ar-f-situacion)' }}>
        {cabecera}

        <div className="ar-sit-strip">
          <span className="ar-badge">{nombre(opts?.sectores, sector)}</span>
          <span className="ar-badge">{nombre(opts?.situaciones, situacion)}</span>
          <span className="ar-badge ar-badge-hot">{nombre(opts?.exigencia, exigencia)}</span>
        </div>

        {/* EL SUELO, A LA VISTA. No se descubre al final. */}
        <p className={`ar-sit-floor ${juzgable ? 'ar-sit-floor-ok' : ''}`}>
          {juzgable ? T.sueloListo : T.suelo(faltanTurnos, faltanPalabras)}
        </p>

        <div className="ar-sit-hilo">
          {turnos.map((t, i) => (
            <div
              key={`${i}-${t.texto.slice(0, 12)}`}
              className={t.de === 'alumno' ? 'ar-sit-mio' : 'ar-sit-suyo'}
            >
              {t.texto}
            </div>
          ))}
          {/* Ocupa el sitio de un turno pero NO es un turno: clase propia, para
              que nada pueda contar el «está pensando» como algo que se dijo. */}
          {esperando && <div className="ar-sit-pensando" aria-live="polite">···</div>}
          <span ref={finDelHilo} />
        </div>

        {error && <div className="ar-fail" style={{ marginTop: '.6rem' }}>{error}</div>}

        {dictando && <div className="ar-transcript">{borrador || T.teEscucho}</div>}

        {aMano && (
          <textarea
            className="ar-sit-teclado"
            rows={3}
            value={borrador}
            placeholder={T.escribeAqui}
            onChange={(e) => setBorrador(e.target.value)}
          />
        )}

        <div className="ar-actions">
          {aMano ? (
            <button
              type="button" className="ar-btn"
              disabled={esperando || !borrador.trim()}
              onClick={() => manda(borrador)}
            >{T.enviar}</button>
          ) : dictando ? (
            <button
              type="button" className="ar-btn ar-btn-rec"
              onClick={() => manda(finalText.current || borrador)}
            >{T.yaDije}</button>
          ) : (
            <button
              type="button" className="ar-btn"
              disabled={esperando}
              onClick={oye}
            >{T.contestar}</button>
          )}

          <button type="button" className="ar-btn ar-btn-ghost" onClick={cierra}>
            {T.cerrar}
          </button>
        </div>
      </div>
    );
  }

  /* ── 3. EL DICTAMEN ─────────────────────────────────────────────────────── */
  if (phase === 'grading') {
    return (
      <div className="ar-screen" style={{ '--ar-mc': 'var(--ar-f-situacion)' }}>
        {cabecera}
        <div className="ar-spin" />
        <p className="ar-lbl" style={{ textAlign: 'center' }}>{T.juzgando}</p>
      </div>
    );
  }

  // El dictamen es tres cosas, no dos: sí, no, y NO SE JUZGÓ.
  //
  // La tercera manda sobre las otras dos, y por eso se mira `sin_juicio` ANTES
  // que `logrado`. Se vio en una captura: sin haber hablado salía «✕ No lo
  // conseguiste» en rojo mientras el propio texto de debajo decía «no te digo si
  // lo lograste porque no habría con qué juzgarlo». El titular acusaba de algo
  // que el cuerpo se negaba a afirmar.
  //
  // Y no es un detalle de tono: un suspenso por no haber jugado es la clase de
  // castigo injusto que hace que un alumno no vuelva. El servidor manda
  // `logrado: false` ahí por prudencia —para que nadie lo lea como aprobado—,
  // así que quien tiene que distinguir es esta pantalla.
  const juzgado = Boolean(verdict) && !verdict.sin_juicio;
  const logrado = juzgado ? verdict.logrado : null;
  const tono = logrado === true ? 'ar-v-ok' : logrado === false ? 'ar-v-bad' : 'ar-v-mid';

  return (
    <div className="ar-screen" style={{ '--ar-mc': 'var(--ar-f-situacion)' }}>
      {cabecera}

      <div className={`ar-verdict ${tono}`} style={{ marginTop: '.9rem' }}>
        <p className="ar-sit-fallo">
          {logrado === true ? T.logrado
            : logrado === false ? T.noLogrado
              : T.sinJuicio}
        </p>

        <p className="ar-said">{verdict?.veredicto}</p>

        {verdict?.evidencia?.length > 0 && (
          <>
            <span className="ar-lbl">{T.conTusPalabras}</span>
            <ul className="ar-list">
              {verdict.evidencia.map((s) => <li key={s} className="ar-plus">“{s}”</li>)}
            </ul>
          </>
        )}

        {verdict?.mejor?.length > 0 && (
          <>
            <span className="ar-lbl">{T.laProxima}</span>
            <ul className="ar-list">
              {verdict.mejor.map((s) => <li key={s} className="ar-minus">{s}</li>)}
            </ul>
          </>
        )}

        <div className="ar-stats">
          <div><b className="ar-num">{verdict?.turnos}</b><span>{T.statTurnos}</span></div>
          <div><b className="ar-num">{verdict?.palabras}</b><span>{T.statPalabras}</span></div>
          <div><b className="ar-num">{Math.round(verdict?.wpm || 0)}</b><span>{T.statRitmo}</span></div>
        </div>

        {verdict?.ai_evaluated === false && juzgado && (
          <p className="ar-note">{T.sinIA}</p>
        )}
      </div>

      <div className="ar-actions">
        <button type="button" className="ar-btn" onClick={otraVez}>{T.otraEscena}</button>
        <button type="button" className="ar-btn ar-btn-ghost" onClick={onExit}>
          {T.volver}
        </button>
      </div>
    </div>
  );
}
